import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button variants derived from the design reference. The sizes are the ones the
 * handoff actually uses; `icon` and `text` are smaller than 44px by design and
 * therefore pair with `hit-44` to keep the touch target large enough.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans transition-colors disabled:pointer-events-none disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:opacity-90',
        outline: 'border border-line bg-transparent text-fg-secondary hover:bg-muted',
        ghost: 'bg-transparent text-fg-tertiary hover:bg-muted',
        text: 'bg-transparent text-fg-muted hover:text-fg',
        destructive: 'bg-transparent text-cal-9 hover:opacity-80',
      },
      size: {
        /** Full-width primary action in sheets and the login card. */
        block: 'h-11 w-full rounded-control text-action font-semibold',
        /** Right-aligned dialog footer buttons on desktop. */
        dialog: 'rounded-[9px] px-3.5 py-2 text-control font-semibold',
        /** Small dark buttons such as "Zeitleiste öffnen" or "+ Person". */
        compact: 'rounded-cell px-2.5 py-1.5 text-chip font-semibold',
        /** Month and year arrows: 30px square. */
        icon: 'size-[30px] rounded-cell border border-line text-fg-tertiary text-sm',
        /** Desktop arrows are a touch smaller. */
        iconSm: 'size-[28px] rounded-cell border border-line text-fg-tertiary text-[13px]',
        /** Inline text action, e.g. "Bearbeiten" or "Abbrechen". */
        text: 'rounded-cell px-1 py-1 text-label',
        /** 40px pair inside alert dialogs. */
        alert: 'h-10 flex-1 rounded-control text-body font-semibold',
      },
    },
    defaultVariants: { variant: 'primary', size: 'block' },
  },
);

export interface ButtonProps
  extends ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild, ...props }: ButtonProps) {
  const Component = asChild ? Slot : 'button';
  return (
    <Component className={cn(buttonVariants({ variant, size }), className)} {...props} />
  );
}

export { buttonVariants };
